import { Account } from '../account.domain';
import { ExchangeRate } from '../exchange-rate.domain';
import { HoldingResponse } from '../holding.domain';

import { DepositFrequency, GoalStatusType, GoalType, LtiAccountStatus, ProjectionUnitCode, LtiProvince } from './lti.enum';
import type { InvestorProfileModelLike } from './lti-shared.domain';

/**
 * These have been created based on what the backend sends to the frontend
 * Please try to keep interface properties alphabetized
 */

export interface InvestmentGoal {
  id?: string;
  creationDate?: Date;
  name?: string;
  currentAge?: number;
  depositAmount?: number;
  depositFrequency?: DepositFrequency;
  extraOneTimeAmount?: number;
  inflationRate?: number;
  investmentObjective?: InvestmentObjective;
  investorProfileModel?: InvestorProfileModelLike;
  partyId?: string;
  profileModelId?: string;
  projectedAmount?: number;
  readinessPct?: number; // TODO: backend should change it to 'readiness'
  roiRate?: number;
  startingAmount?: number;
  subType?: GoalType;
  status?: GoalStatusType;
  targetAmount?: number;
  targetAge?: number;
  targetDate?: Date;
}

export interface RetirementGoal extends InvestmentGoal {
  additionalRetirementIncome?: number;
  afterRetirementROIRate?: number;
  annualRateIncreasePercentage?: number;
  annualRetirementIncomeAmount?: number;
  annualCPPAmount?: number;
  annualOASAmount?: number;
  annualQPPAmount?: number;
  beforeRetirementROIRate?: number;
  beforeRetirementTaxRate?: number;
  currentAnnualIncome?: number;
  includeCPP?: boolean;
  includeOAS?: boolean;
  includeQPP?: boolean;
  lifeExpectancy?: number;
  province?: LtiProvince;
  retirementAge?: number;
  retirementLifestylePercentage?: number;
}

export interface MajorPurchaseGoal extends InvestmentGoal {
  purchaseDescription?: string;
}

export interface InvestmentObjective {
  currentInvestmentAmount?: number;
  totalTargetAmount?: number;
  totalShortfallAmount?: number;
  targetDate?: Date;
  targetAge?: number;
  objectiveDetails?: Array<ObjectiveDetails>;
  projectionUnit?: ProjectionUnitCode;
  recommendation?: Recommendation;
  onTrack?: boolean;
}

export interface ObjectiveDetails {
  age?: number;
  asOfDate?: Date;
  projectedAmount?: number;
}

export interface Recommendation {
  recurringDepositAmount?: number;
  depositFrequency?: DepositFrequency;
  oneTimeDepositAmount?: number;
  targetAge?: number;
}

export interface AssumptionsData {
  goalId?: string;
  subType?: GoalType;
  includeCPP?: boolean;
  includeQPP?: boolean;
  includeOAS?: boolean;
  additionalRetirementIncome?: number;
  inflationRate?: number;
  lifeExpectancy?: number;
  cppAmount?: number;
  qppAmount?: number;
  oasAmount?: number;
  investorProfileModel?: InvestorProfileModelLike;
  retirementAge?: number;
}

export interface PlanSummary {
  targetAmount?: number;
  projectedAmount?: number;
  subType?: GoalType;
  status?: GoalStatusType;
  title: boolean;
  targetAge?: number;
}

export interface LtiAccount extends Account {
  ltiAgreementId?: string;
  ltiStatus?: LtiAccountStatus;
  balanceDate?: Date;
  fxRate?: ExchangeRate;
  totalBalance?: number;
}

export interface LtiConfig {
  dashboardPageSize?: number;
  maxGoals?: number;
}

export interface AdjustGoalData {
  contributionAmount?: number;
  contributionFrequency?: DepositFrequency;
  targetAge?: number;
  targetAmount?: number;
}

export interface LtiPortfolio {
  holdingsGroups: Array<HoldingsGroup>;
  goalId: string;
  asOfDate: Date;
  exchangeRate: ExchangeRate;
}

export interface HoldingsGroup {
  holdingsGroupDetails: HoldingsGroupDetails;
  records: Array<HoldingResponse>;
}

export interface HoldingsGroupDetails {
  account: LtiAccount;
  cashBalance?: number;
  cashPercentOfAccount?: number;
}

export interface PortfolioHolding extends HoldingResponse {
  footnotes: {
    price?: number;
    bookValue?: number;
    amountConverted?: number;
  };
}

export enum DiPortfolioHoldingsTableColumn {
  NAME = 'name',
  BUY_SELL = 'buySell',
  QUANTITY = 'quantity',
  MARKET_VALUE = 'marketValue',
  BOOK_VALUE = 'bookValue',
  GAIN_LOSS_UNREALIZED = 'unrealizedGainOrLoss',
  BID = 'bid',
  ASK = 'ask',
  PERCENT_OF_ACCOUNT = 'percentOfAccount'
}
